// Recursos FHIR que dejan las anamnesis discretas 002 y 003 al guardar (Fase 7, Tanda 17). Reproducen lo que hacía
// `extraerConsultaExterna` del motor de Questionnaire (motivo → `Encounter.reasonCode`, un `Condition` por diagnóstico,
// `Observation` de hallazgos de examen físico, `AllergyIntolerance` solo en 003) y suman la `Composition` que marca la
// anamnesis como completada para las verificaciones de completitud y calidad de la HC. Todo va en UNA transacción: un
// fallo no deja datos a medias ni duplica al reintentar, y la `Composition` nunca existe sin sus datos.
import type { CodeableConcept, Encounter, Patient, Reference } from '@medplum/fhirtypes';
import type { MedplumClient } from '@medplum/core';
import { CLINICAL_STATUS_ALERGIA_ACTIVA, esRespuestaAlergiaNegativa } from '../extractors';
import { SISTEMA_FORMULARIOS_MSP } from './bloquesAnamnesisDatos';
import { observacionesDeTomas, type TomaSignos } from './signosVitalesToma';
import { LoteTransaccional } from './transaccionFhir';

export interface DatosGuardadoAnamnesis {
  readonly codigo: '002' | '003';
  readonly subject: Reference<Patient>;
  readonly encounter?: Reference<Encounter>;
  /** `Encounter.reasonCode` — el motivo (002) o los motivos numerados unidos (003). */
  readonly motivo: string;
  readonly diagnosticos: readonly CodeableConcept[];
  /** "Hallazgos de examen físico" (vacío = no se crea el Observation). */
  readonly hallazgos: string;
  /** Solo 003: descripciones de alergia a medicamentos / otras alergias marcadas. */
  readonly alergias?: readonly string[];
  /** Constantes vitales escritas en el formulario: cada valor numérico crea su `Observation` (Signos Vitales). */
  readonly tomas?: readonly TomaSignos[];
}

const TITULOS = { '002': 'Consulta externa — Anamnesis y examen físico', '003': 'Hospitalización — Anamnesis y examen físico' } as const;

export async function guardarRecursosAnamnesis(medplum: MedplumClient, d: DatosGuardadoAnamnesis): Promise<void> {
  const ahora = new Date().toISOString();
  // `Composition.author` es obligatorio: se valida antes de escribir nada.
  const perfil = medplum.getProfile();
  if (!perfil) throw new Error('No se pudo identificar al profesional que guarda la anamnesis. Cierre sesión y vuelva a ingresar.');

  const lote = new LoteTransaccional();
  if (d.motivo.trim() && d.encounter) {
    const actual = await medplum.readReference(d.encounter);
    lote.actualizarEncounterAbierto({ ...actual, id: actual.id!, reasonCode: [{ text: d.motivo.trim() }] });
  }
  for (const code of d.diagnosticos) {
    lote.crear({ resourceType: 'Condition', subject: d.subject, encounter: d.encounter, code, recordedDate: ahora });
  }
  if (d.hallazgos.trim()) {
    lote.crear({ resourceType: 'Observation', status: 'final', subject: d.subject, encounter: d.encounter, code: { text: 'Hallazgos de examen físico' }, valueString: d.hallazgos.trim(), effectiveDateTime: ahora });
  }
  for (const texto of d.alergias ?? []) {
    if (!texto.trim() || esRespuestaAlergiaNegativa(texto)) continue;
    lote.crear({ resourceType: 'AllergyIntolerance', patient: d.subject, clinicalStatus: CLINICAL_STATUS_ALERGIA_ACTIVA, code: { text: texto.trim() }, recordedDate: ahora });
  }
  for (const obs of observacionesDeTomas(d.tomas ?? [], d.subject, d.encounter, ahora)) lote.crear(obs);
  lote.crear({
      resourceType: 'Composition',
      status: 'final',
      type: { coding: [{ system: SISTEMA_FORMULARIOS_MSP, code: d.codigo }], text: TITULOS[d.codigo] },
      subject: d.subject,
      encounter: d.encounter,
      date: ahora,
      author: [{ reference: `${perfil.resourceType}/${perfil.id}` }],
      title: TITULOS[d.codigo],
  });

  await lote.enviar(medplum);
}
